import React, { useMemo } from "react";
import { ChartPie } from "lucide-react";
import type { ChartConfig } from "@/components/ui/chart";
import { AnalyticsDonutChartArea } from "../shared/AnalyticsDonutChartArea";
import {
  REVENUE_CHART_CARD_ICON_COLOR,
  REVENUE_LEGEND_AMOUNT_COLOR,
  REVENUE_LEGEND_PERCENT_COLOR,
} from "./constants";
import { formatRevenueCurrency } from "./revenueCurrency";
import type { DonutSegment } from "./mockData";
import { RevenueChartCard } from "./RevenueChartCard";

interface RevenueDonutCardProps {
  title: string;
  segments: DonutSegment[];
  totalLabel: string;
  currency: string;
  className?: string;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

function buildChartConfig(segments: DonutSegment[]): ChartConfig {
  return segments.reduce<ChartConfig>((config, segment) => {
    config[segment.key] = {
      label: segment.name,
      color: segment.fill,
    };
    return config;
  }, {});
}

export const RevenueDonutCard: React.FC<RevenueDonutCardProps> = ({
  title,
  segments,
  totalLabel,
  currency,
  className,
  onExport,
  exportDisabled,
}) => {
  const total = useMemo(
    () => segments.reduce((sum, segment) => sum + segment.value, 0),
    [segments],
  );
  const chartConfig = useMemo(() => buildChartConfig(segments), [segments]);

  return (
    <RevenueChartCard
      title={title}
      width="fixed"
      icon={<ChartPie />}
      iconColor={REVENUE_CHART_CARD_ICON_COLOR}
      className={className}
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <div className="flex flex-col items-center gap-3">
        <AnalyticsDonutChartArea
          segments={segments}
          total={total}
          chartConfig={chartConfig}
          centerPrimary={totalLabel}
          centerSecondary="Total"
        />

        <ul className="flex w-full flex-col gap-3">
          {segments.map((segment) => {
            const percent =
              total > 0 ? Math.round((segment.value / total) * 100) : 0;

            return (
              <li
                key={segment.key}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: segment.fill }}
                  />
                  <span className="shrink-0 text-sm leading-normal text-[#171717]">
                    {segment.name}
                  </span>
                </div>
                <span className="shrink-0 text-sm font-normal leading-none">
                  <span style={{ color: REVENUE_LEGEND_PERCENT_COLOR }}>
                    {percent}% (
                  </span>
                  <span style={{ color: REVENUE_LEGEND_AMOUNT_COLOR }}>
                    {formatRevenueCurrency(segment.value, currency)}
                  </span>
                  <span style={{ color: REVENUE_LEGEND_PERCENT_COLOR }}>)</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </RevenueChartCard>
  );
};
